const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface CourseWritingUnit {
  id: string;
  day_number: number;
  unit_type: string;
  program_id?: string | null;
  duration_minutes?: number | null;
}

function validDay(value: number) {
  return Number.isSafeInteger(value) && value > 0 && value <= 2147483647;
}

export function getCourseWritingContext(params: URLSearchParams) {
  const journeyId = params.get('journeyId');
  const unitId = params.get('journeyUnit');
  const rawDay = params.get('journeyDay');
  const day = Number(rawDay);
  if (
    params.get('journeyExperience') !== 'course_v1'
    || !journeyId || !UUID.test(journeyId)
    || !unitId || !UUID.test(unitId)
    || !rawDay || !/^\d+$/.test(rawDay) || !validDay(day)
  ) return null;
  return { journeyId, unitId, day };
}

export interface CoursePreviewNavigation {
  preview: true;
  previewFrom: 'admin' | 'creator';
  previewSnapshot?: string;
}

/** Intent only. The authenticated preview boundary verifies authorization. */
export function getCoursePreviewNavigation(params: URLSearchParams): CoursePreviewNavigation | null {
  if (params.get('preview') !== '1') return null;
  const snapshot = params.get('previewSnapshot');
  return { preview: true, previewFrom: params.get('previewFrom') === 'admin' ? 'admin' : 'creator', ...(snapshot && UUID.test(snapshot) ? { previewSnapshot: snapshot } : {}) };
}

export function buildCoursePreviewPath(journeyId: string, from: 'admin' | 'creator', day?: number, unitId?: string, snapshot?: string) {
  const params = new URLSearchParams({ preview: '1', previewFrom: from });
  if (snapshot && UUID.test(snapshot)) params.set('previewSnapshot', snapshot);
  if (day && validDay(day)) params.set('day', String(day));
  if (unitId && UUID.test(unitId)) params.set('unit', unitId);
  return `/journey/${journeyId}/learn?${params}`;
}

/** Uses the full existing writing players. Query context is navigation, never access proof. */
export function buildCourseWritingPath(unit: CourseWritingUnit, journeyId: string, preview?: CoursePreviewNavigation | null): string | null {
  if (!UUID.test(journeyId) || !UUID.test(unit.id) || !validDay(unit.day_number)) return null;
  if (unit.unit_type !== 'powerprompt' && unit.unit_type !== 'freestyle') return null;
  if (unit.unit_type === 'powerprompt' && (!unit.program_id || !UUID.test(unit.program_id))) return null;

  const params = new URLSearchParams({
    journeyExperience: 'course_v1', journeyId,
    journeyUnit: unit.id, journeyDay: String(unit.day_number),
  });
  if (preview) {
    params.set('preview', '1');
    params.set('previewFrom', preview.previewFrom);
    if (preview.previewSnapshot) params.set('previewSnapshot', preview.previewSnapshot);
  }
  if (unit.unit_type === 'powerprompt') {
    params.set('autostart', '1');
    return `/session/${unit.program_id}?${params}`;
  }
  if (Number.isInteger(unit.duration_minutes) && unit.duration_minutes! >= 1 && unit.duration_minutes! <= 120) {
    params.set('suggestedMinutes', String(unit.duration_minutes));
  }
  return `/freestyle?${params}`;
}

/** A receipt must come from the player's successful database write, never from its query string. */
export function getCourseReturnPath(params: URLSearchParams, completedSessionId?: string | null): string | null {
  const context = getCourseWritingContext(params);
  if (!context) return null;
  const query = new URLSearchParams({ day: String(context.day), unit: context.unitId });
  const preview = getCoursePreviewNavigation(params);
  if (preview) {
    query.set('preview', '1');
    query.set('previewFrom', preview.previewFrom);
    if (preview.previewSnapshot) query.set('previewSnapshot', preview.previewSnapshot);
  } else if (completedSessionId && UUID.test(completedSessionId)) query.set('completedSession', completedSessionId);
  return `/journey/${context.journeyId}/learn?${query}`;
}

export function getCourseSuggestedMinutes(params: URLSearchParams): number | null {
  if (!getCourseWritingContext(params)) return null;
  const raw = params.get('suggestedMinutes');
  if (!raw || !/^\d+$/.test(raw)) return null;
  const minutes = Number(raw);
  return Number.isInteger(minutes) && minutes >= 1 && minutes <= 120 ? minutes : null;
}
